<template>
  <article class="page alibaba">
    <router-link to="/" title="Back Home" class="logo logo-absolute">
      <AnimeLogo primaryColor="#00BAFF" secondaryColor="#00BAFF" />
    </router-link>
    <section
      id="banner"
      class="banner section"
      v-observe-visibility="{
        callback: bannerChanged,
        intersection: { rootMargin: '-204px 0px 0px 0px' }
      }"
    >
      <hr class="stroke ali-gradient" />
      <h1 class="title">{{ content.title }}</h1>
      <h3 class="sub-headline">{{ content.role }}</h3>
      <div class="team">
        <h3 class="headline">Team</h3>
        <ul>
          <li class="norm-height">
            Xiaoming Wang
            <span class="small tab-left">Design Manager</span>
          </li>
          <li class="norm-height">
            Xiao Tan
            <span class="small tab-left">Mentor</span>
          </li>
          <li class="norm-height">
            Ya Zheng
            <span class="small tab-left">Senior Designer</span>
          </li>
        </ul>
      </div>
      <div class="role">
        <h3 class="headline">Role</h3>
        <ul class="list-sum list-alibaba">
          <li>Prototype & Iterate: Low-Fi → Refined UI.</li>
          <li>Refine Design System / Design Pattern.</li>
          <li>Web Accessibility Design.</li>
          <li>Hand-Off Specifications & Communications.</li>
        </ul>
      </div>
    </section>
    <figure id="ali-banner-bg" class="banner-bg r-1-3 grid">
      <svg viewBox="0 0 1002 672" class="ali-banner-pic">
        <title>Alibaba Cloud - Banner Picture</title>
        <image
          id="datav-tab"
          x="489"
          y="288"
          width="489"
          height="369"
          href="@/assets/work/alibaba/banner/datav-tab.png"
        />
        <image
          id="datav-workspace"
          x="40"
          y="0"
          width="684"
          height="337"
          href="@/assets/work/alibaba/banner/datav-workspace.png"
        />
      </svg>
      <img
        src="@/assets/work/alibaba/banner/alibaba-logo.svg"
        alt="Alibaba Cloud Design Logo"
        class="banner-logo"
      />
    </figure>
    <section id="overview" class="overview r-2 section">
      <hr class="divider" />
      <a
        href="#overview"
        class="outline theme-alibaba sticky"
        v-scroll-to="'#overview'"
        >Overview</a
      >
      <h2 class="headline">Alibaba Cloud DataV Platform</h2>
      <h4 class="subtitle">Desktop Developer Toolkit</h4>
      <p class="content">
        Working with senior designers, developers, and the product manager, I
        designed the desktop toolkit which is the extension of the larger DataV
        web data visualization platform. The toolkit serves data visualization
        developers who use DataV for visualization component development to work
        seamlessly in the local-to-web workflow. Through iterations and constant
        feeback, I delivered a
        <strong class="highlight">hi-fi designs</strong> with detailed
        interactions and
        <strong class="highlight"
          >styleguide-compliant UI visual designs</strong
        >
        for implementation.
      </p>
      <aliScreens />
      <div id="workflow" class="heading section-break">
        <p class="overline small text-second">Workflow</p>
        <h3 class="headline">User Journey Map</h3>
      </div>
      <p class="content">
        My work was focused on the desktop app section of the workflow. I also
        included the web part of the experience which I was not working on but
        is integral to the main workflow as a whole.
      </p>
      <p class="content">
        In short, the users (visualization component developers) use the tool
        to:
      </p>
      <ol class="content">
        <li>
          <strong class="highlight">Create / Import</strong> new visualization
          component locally;
        </li>
        <li>
          <strong class="highlight">Preview & Test</strong> the visualization
          component locally;
        </li>
        <li>
          <strong class="highlight">Deploy</strong> the visualization component
          for production.
        </li>
        <li>
          <strong class="highlight">Manage</strong> local visualization
          components and online visualization deployments.
        </li>
      </ol>
      <div class="ali-workflow min-width padding2">
        <object
          type="image/svg+xml"
          :data="svgLib.workflow"
          id="ali-workflow"
        />
      </div>
    </section>
    <section id="iteration" class="iteration section">
      <hr class="divider" />
      <a
        href="#iteration"
        class="outline theme-alibaba sticky"
        v-scroll-to="'#iteration'"
        >Iteration</a
      >
      <h2 class="headline">Iterative Tool Design Process</h2>
      <h4 class="subtitle">from Low-Fi to Hi-Fi</h4>
      <p class="content">
        The design process for DataV started from a seemingly standard create /
        preview & test / deploy process with the key goal to create a
        <strong class="highlight">local</strong> development and testing
        environment for developer to deploy components
        <strong class="highlight">online</strong> eventually.
      </p>
      <transition
        name="fade"
        mode="out-in"
        @beforeLeave="beforeLeave"
        @enter="enter"
        @afterEnter="afterEnter"
      >
        <aliEvolution
          v-if="iterationZoomed"
          class="change-height"
          @click.native="toggleIterationView(false)"
        />
        <aliIterations
          v-else
          class="change-height"
          @click.native="toggleIterationView(true)"
        />
      </transition>
    </section>
    <section id="ui-design" class="ui-design section">
      <hr class="divider" />
      <a
        href="#ui-design"
        class="outline theme-alibaba sticky"
        v-scroll-to="'#ui-design'"
        >UI Design</a
      >
      <h2 class="headline no-subtitle">DataV Design System Refresh</h2>
      <p class="content">
        As part of the hand-off process, a lot of the effort was put into
        building a robust design system for smooth hand-off. This coincided with
        the product-wide UI refresh effort, where I was able to learn and
        contribute to the UI design system in its
        <strong class="highlight">layer (style)</strong> →
        <strong class="highlight">element (symbol)</strong> →
        <strong class="highlight">screen (artboard)</strong> hierarchy.
      </p>
      <aliSymbols />
      <p class="content">
        My key contribution to the system was that I was able to take initiative
        to push for the inclusion of
        <strong class="highlight">web accessibility guidelines</strong>. This
        turned out to be one of the happy surprises during this internship which
        started from an unintentional observation.
      </p>
      <p class="overline small text-second">Observation</p>
      <h2 class="keyline">
        Many of the external monitors developers use for coding are not great at
        displaying dark theme, leading to a washed-out UI and texts nothing near
        as legible as we intended them to be.
      </h2>
      <div class="heading section-break">
        <p class="overline small text-second">Color Contrast</p>
        <h3 class="headline">Color Palette System</h3>
      </div>
      <p class="content">
        The most obvious starting point for the redesign would be in improving
        color contrast and that was my starting point. The contrast for the
        existing UI was OK but it was mostly based haphazardly on intuition. I
        needed to be able to quantify contrast as a guideline and systematically
        generate colors as a system. For the former, I relied on the WCAG 2
        standard to ensure
        <strong class="highlight">level AA</strong> compliant
        <strong class="highlight">color contrasts</strong> for all active
        elements.
      </p>
      <aliColors />
      <p class="content">
        To allow future designers and developers to better utilize and expand
        the color system, I used Material Design Color System and Ant Design
        Color System as references to create a systematic way to generate new
        colors if needed using HSV colors, meanwhile maintaining the level of
        color contrast to ensure content legibility.
      </p>
      <div class="heading section-break">
        <p class="overline small text-second">Visual Hierarchy</p>
        <h3 class="headline">Alignment & Interaction States</h3>
      </div>
      <p class="content">
        During my work on the desktop toolkit app, I spent a lot of time
        refining the component properties panel where there is a lot of
        information on display side-by-side. My main approach was to apply
        Gestalt Principles and use
        <strong class="highlight">alignment and indentation</strong> to create a
        clear visual hierarchy.
      </p>
      <p class="content">
        As for actionable elements, I applied the redesigned color system and
        used color to create a visual hierarchy among actionable elements and
        their different interaction states. Inspired by the surface and
        elevation approach of Material Design, I created a simpler system where
        I applied the appropriate color to simulate a similar spatial relation
        for different states and levels of importance.
      </p>
      <figure id="ali-layout" class="ali-layout">
        <img
          class="c-1--1"
          src="@/assets/work/alibaba/content/ui-layout.svg"
          alt="UI element spatial relations"
        />
        <figcaption class="c-1--1 line">
          Actionable elements are designed as a poped out element that elevates
          from the background surface and cast shadows on the background. The
          higher the importance of a state (e.g. hover), the higher the
          elevation and lighter the element color. Focus states are indicated by
          a halo that is lighting up at the bottom of the element, replacing the
          shadow cast on the background.
        </figcaption>
      </figure>
      <div class="ali-library fixed-grid min-width">
        <figure
          id="ali-library"
          v-observe-visibility="{
            callback: darkSection,
            intersection: { rootMargin: '-216px 0px -492px 0px' }
          }"
        >
          <img
            class="c-1--1 r-1-2"
            src="@/assets/work/alibaba/content/ui-library.svg"
            alt="UI library - alignment and interactive states"
          />
          <figcaption class="c-3-5 r-1-2 bottom line dark margin2-b">
            UI library for preview properties panel and elements with refined
            alignment and comprehensive interactive states for actionable
            elements.
          </figcaption>
        </figure>
      </div>
      <p class="content">
        Another key focus was to refine all the actionable elements to include
        designs for all interaction states. Being a productivity tool with many
        intermediate to expert users, keyboard navigation and shortcuts were
        crucial for efficiency. Therefore, I paid extra attention to the
        <strong class="highlight">hover and focus states</strong> to provide
        enough salient visual clues to help quickly identify and locate
        actionable elements, as well as improving keyboard accessibility (more
        perceivable & operable).
      </p>
    </section>
    <section id="exploration" class="exploration section">
      <hr class="divider" />
      <a
        href="#exploration"
        class="outline theme-alibaba sticky"
        v-scroll-to="'#exploration'"
        >Exploration</a
      >
      <h2 class="headline">Exploring Design Tools</h2>
      <h4 class="subtitle">Motion Design with Adobe XD</h4>
      <p class="content">
        During my process of designing hi-fi prototype, I experimented with
        Adobe XD for interface motion designs to help me explain interactions
        through animations.
      </p>
      <figure id="ali-video" class="ali-video grid">
        <figure class="video-fig">
          <div class="aspect a4-3">
            <video
              class="top"
              poster="@/assets/work/alibaba/video/login.png"
              src="@/assets/work/alibaba/video/login.mp4"
              autoplay
              playsinline
              muted
              loop
            >
              Your browser does not support video tag.
            </video>
          </div>
          <figcaption class="xsmall text-second">
            <strong>Log In Process</strong>
            <br />Log into welcome page → Switch user → Open workspace
          </figcaption>
        </figure>
        <figure class="video-fig">
          <div class="aspect a4-3">
            <video
              class="top"
              poster="@/assets/work/alibaba/video/import.png"
              src="@/assets/work/alibaba/video/import.mp4"
              autoplay
              playsinline
              muted
              loop
            >
              Your browser does not support video tag.
            </video>
          </div>
          <figcaption class="xsmall text-second">
            <strong>Import Process</strong>
            <br />Start import → Drop in file → Set up component info → Undo /
            redo import
          </figcaption>
        </figure>
        <figure class="video-fig">
          <div class="aspect a4-3">
            <video
              class="top"
              poster="@/assets/work/alibaba/video/preview.png"
              src="@/assets/work/alibaba/video/preview.mp4"
              autoplay
              playsinline
              muted
              loop
            >
              Your browser does not support video tag.
            </video>
          </div>
          <figcaption class="xsmall text-second">
            <strong>Component Preview</strong>
            <br />Resize component → Zoom in / out → Hide Side Panel
          </figcaption>
        </figure>
      </figure>
    </section>
    <section id="retrospective" class="retrospective section">
      <hr class="divider" />
      <a
        href="#retrospective"
        class="outline theme-alibaba sticky"
        v-scroll-to="'#retrospective'"
        >Retrospective</a
      >
      <h2 class="headline no-subtitle">Reflections & Takeaways</h2>
      <h3 class="headline heading">Every observation counts.</h3>
      <p class="content">
        One theme that was strikingly common during my internship was how often
        design ideas came from just small observations. The first thing I
        learned from this experience is to appreciate the power of a seemingly
        small observation. Make every observation counts. Ask why. Dig deeper. A
        good question is the first step to getting a good solution.
      </p>
      <h3 class="headline heading">Design systematically.</h3>
      <p class="content">
        Having been working with a large and technical design project like
        DataV, I was constantly reminded to design for robustness. I learned to
        design with the system in mind and always ask how one design decision
        can affect other parts of the system and its overall experience. A
        design that has a coherent logic can handle edge cases much better than
        an ad-hoc solution.
      </p>
      <h3 class="headline heading">Great details scale well.</h3>
      <p class="content">
        Designing in this internship was a process of constantly crafting every
        detail in the design. Learning from and working with a living design
        system helped me appreciate the craft that went behind each and every
        detail to allow them to scale in the product designs.
      </p>
    </section>
  </article>
</template>

<style lang="scss">
@import '@/styles/base.scss';

.alibaba {
  .tab-left {
    color: $alibaba-tonedown;
  }

  .banner {
    background-image: linear-gradient(
      90deg,
      $alibaba-highlight 0%,
      $alibaba-theme 100%
    );
  }

  .highlight:before {
    background: rgba($alibaba-theme, 0.5);
  }

  figcaption:before {
    background-color: $alibaba-theme;
  }

  .quick-link {
    color: $alibaba-theme;
    font-weight: 500;
    text-decoration-color: $alibaba-theme;
  }
  .down-arrow {
    position: relative;
    display: inline-block;
    &:hover:after,
    &:focus:after {
      content: '↓';
      text-decoration: none;
      font-size: $h2-size;
      font-weight: 300;
      display: block;
      position: absolute;
      bottom: -$line-height - $baseline;
      width: 100%;
    }
  }
}

.ali-gradient {
  background-image: linear-gradient(
    90deg,
    $alibaba-theme 0%,
    rgba($alibaba-highlight, 0) 100%
  );

  &.stroke {
    background-color: transparent;
    position: absolute;
    top: $logo-pad-top + $page-padding-top;
    left: $logo-width + $body-padding;
  }
}

.ali-banner-pic {
  grid-column: 2 / -2;
  margin-top: $baseline;
  min-width: $small-width * 0.8;
}
.ali-project-timeline {
  grid-column: 1 / -1;
}
.ali-workflow {
  grid-column: 1 / -1;
}
.ali-layout {
  grid-column: 2 / -2;
}
.ali-models {
  grid-column: 1 / -1;

  .ali-model-img {
    grid-column: 2 / -2;
  }
  .ali-model-explain {
    grid-column: 2 / span 2;
  }
  .ali-model-compare {
    grid-column: -4 / -2;
  }
  .ali-model-diagram {
    grid-column: 2 / span 2;
  }
  .ali-model-transfer {
    grid-column: 2 / -2;
  }

  .ali-model-caption {
    grid-column-end: -2;
    grid-row-start: 1;
    margin-bottom: $baseline;
  }
}
.ali-video {
  grid-column: 1 / -1;
  overflow-x: hidden;

  .video-fig {
    grid-column: 1 / -1;
    z-index: -1;

    figcaption {
      padding: $baseline $column-gap 0;
    }
  }

  video {
    width: 100%;
  }
}

// Media query
@media only screen and (min-width: $small-width) {
  .ali-banner-pic {
    margin-top: 0;
    grid-column-start: 3;
  }
  .ali-layout {
    grid-column-start: 3;
  }
  .ali-models {
    .ali-model-img {
      grid-column-start: 3;
    }
    .ali-model-explain {
      grid-column-start: -5;

      p:last-child {
        margin-bottom: $line-height;
      }
    }
    .ali-model-compare {
      grid-column-start: -3;
      grid-row-end: span 2;
    }
    .ali-model-diagram {
      grid-column-start: -5;
    }
    .ali-model-transfer {
      grid-column-start: -5;
    }
  }
  .ali-video {
    .video-fig {
      grid-column: 3 / span 3;

      figcaption {
        padding: $baseline 0 0;
      }
    }
  }
}
@media only screen and (min-width: $mid-width) {
  .ali-layout {
    grid-column-start: 4;
  }
  .ali-models {
    .ali-model-img {
      grid-column: 4 / -3;
    }
    .ali-model-explain {
      grid-column-start: -6;
    }
    .ali-model-compare {
      grid-column: -4 / span 2;
    }
    .ali-model-diagram {
      grid-column-start: -6;
    }
    .ali-model-transfer {
      grid-column: -6 / -3;
    }
  }
  .ali-video {
    .video-fig {
      grid-column-start: 4;
    }
  }
}
@media only screen and (min-width: $large-width) {
  .ali-banner-pic {
    grid-column-start: 4;
  }
  .ali-project-timeline {
    grid-column: 3 / -2;
  }
  .ali-layout {
    grid-column: 5 / -3;
  }
  .ali-models {
    .ali-model-img {
      grid-column: 3 / span 2;
      grid-row-end: span 2;
    }
    .ali-model-explain {
      grid-column-start: -7;
    }
    .ali-model-compare {
      grid-column-start: -5;
    }
    .ali-model-diagram {
      grid-column-start: -7;
    }
    .ali-model-transfer {
      grid-column-start: -7;
    }
  }
  .ali-video {
    .video-fig {
      grid-column-end: span 2;

      &:first-child {
        grid-column-start: 2;
      }
      &:nth-child(2) {
        grid-column-start: 5;
      }
      &:nth-child(3) {
        grid-column-start: 8;
      }

      & > .aspect {
        margin-bottom: 40%;
      }

      video {
        width: 150%;
      }
    }
  }
}
</style>

<script>
import AnimeLogo from '@/components/animation/logo.vue'
import aliScreens from '@/views/work/alibaba/alibaba-screens.vue'
import aliIterations from '@/views/work/alibaba/alibaba-iterations.vue'
import aliEvolution from '@/views/work/alibaba/alibaba-evolution.vue'
import aliSymbols from '@/views/work/alibaba/alibaba-symbols.vue'
import aliColors from '@/views/work/alibaba/alibaba-colors.vue'

import anime from 'animejs'

export default {
  name: 'alibaba',
  data() {
    return {
      content: this.$store.state.work.alibaba.content,
      bannerVisible: null,
      darkVisible: null,
      prevHeight: 0,
      iterationZoomed: true,
      svgLib: {
        workflow: require('@/assets/work/alibaba/content/workflow.svg')
      }
    }
  },
  components: {
    AnimeLogo,
    aliScreens,
    aliIterations,
    aliEvolution,
    aliSymbols,
    aliColors
  },
  methods: {
    bannerChanged(isVisible, entry) {
      if (this.bannerVisible === null || this.bannerVisible !== isVisible) {
        this.bannerVisible = isVisible
        // console.log('banner visible: ' + this.bannerVisible)
        this.$emit('banner', this.bannerVisible, entry)
      }
    },
    darkSection(isVisible, entry) {
      if (this.darkVisible === null || this.darkVisible !== isVisible) {
        this.darkVisible = isVisible
        // console.log('dark visible: ' + this.darkVisible)
        this.$emit('darksec', this.darkVisible, entry)
      }
    },
    beforeLeave(element) {
      this.prevHeight = getComputedStyle(element).height
      // console.log(this.prevHeight)
    },
    enter(element) {
      const enterHeight = getComputedStyle(element).height

      element.style.height = this.prevHeight
      // console.log(enterHeight)

      anime({
        targets: element,
        height: enterHeight,
        easing: 'easeInOutQuad',
        duration: 500
      }).play()
    },
    afterEnter(element) {
      element.style.height = 'auto'
    },
    toggleIterationView(zoomed) {
      this.iterationZoomed = zoomed
    }
  },
  mounted() {
    this.$store.commit('changeWorkLinkColor', '#00BAFF')
  }
}
</script>
